<script setup lang="ts">
import type { NovaSearchResultGroupProps } from './NovaSearchResultGroup.types'
import { type SearchAllPost, type Hashtag } from '@store'
import type { ShowPostDetailModalParams } from '@components/NovaBoxPost/NovaBoxPost.types'
import { ROUTES } from '@configs'

const props = defineProps<NovaSearchResultGroupProps>()

const route = useRoute()
const { gtEvent } = useGoogleTag()
const { t } = useI18n()
const searchStore = useSearchStore()
const containerRef = ref<Element | null>(null)

const handleOnGoSearchDetailResult = () => {
  if (!props.items.length) return

  let url = ''
  switch (props.searchItemType) {
    case 'comment':
      url = ROUTES.SEARCH_DETAIL_COMMENT.path
      break
    case 'premium':
      url = ROUTES.SEARCH_DETAIL_NOVAPLUS.path
      break
    case 'hash':
      url = ROUTES.SEARCH_DETAIL_HASHTAG.path
      break
    case 'user':
      url = ROUTES.SEARCH_DETAIL_USER.path
      break
    case 'post':
    default:
      url = ROUTES.SEARCH_DETAIL.path
      break
  }
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '결과 모두 보기 클릭',
    eventLabel: t('seeAllResult'),
    eventSlot: props.searchItemType,
    eventI18nAddr: useKoreanTranslation('seeAllResult'),
    eventComponent: 'Button',
  })
  useNavigations({
    url,
    query: {
      keyword: searchStore.keyword,
    },
  })
}

const handleOnShowPostDetail = (params: ShowPostDetailModalParams) => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '포스트 상세보기',
    eventLabel: '',
    eventSlot: `검색 > ${props.searchItemType}`,
    eventI18nAddr: '',
    eventComponent: 'Modal',
    postId: params.postId,
  })
  useNavigations({
    url: useRoutePathIdChange(ROUTES.SEARCH_POST_MODAL.path, {
      id: String(params.postId),
    }),
    query: route.query,
  })
  // modalShow(modalsName.MODAL_POST_DETAIL, params)
}
</script>

<template>
  <div
    ref="containerRef"
    :class="['search-result-group', `type-${searchItemType}`]"
  >
    <button
      type="button"
      :class="['search-result-group-header', { disabled: !items.length }]"
      @click="handleOnGoSearchDetailResult"
    >
      <span
        v-dompurify-html="t(`searchResultItemType.${searchItemType}`)"
        class="label"
      />
      <span class="go-detail-indicator">
        <span>{{ t('seeAllResult') }}</span>
        <NovaIcon :icon="{ type: 'outline', name: 'arrow-right' }" :size="20" />
      </span>
    </button>

    <NovaBoxEmptyMessage
      v-if="!items.length"
      :message="
        t('statusMessages.search.empty', { keyword: searchStore.keyword })
      "
    />

    <div
      v-if="
        (searchItemType === 'premium' ||
          searchItemType === 'post' ||
          searchItemType === 'comment') &&
        items.length
      "
      class="search-result-items"
    >
      <NovaSearchResultItemPost
        v-for="source in (items as SearchAllPost[])"
        :key="source.cmtyNttSn"
        :source="source"
        @click="handleOnShowPostDetail({ postId: source.cmtyNttSn })"
      />
    </div>

    <div
      v-if="searchItemType === 'hash' && items.length"
      class="search-result-items"
    >
      <NovaSearchResultItemHashtag
        v-for="source in (items as Hashtag[])"
        :key="source.hashtagSn"
        :source="source"
      />
    </div>

    <div
      v-if="searchItemType === 'user' && items.length"
      class="search-result-items"
    >
      <NovaRecyclerGrid
        v-if="items.length"
        :items="items"
        :is-last="true"
        :is-loading="false"
        :is-error="false"
        :is-refresh="false"
        :key-name="'userSn'"
        :page-mode="true"
        :error-message="t('statusMessages.creators.error')"
        :responsive="{
          mobile: { columns: 3, rowGap: 12, columnGap: 12 },
          tablet: { columns: 3, rowGap: 12, columnGap: 12 },
          desktop: { columns: 3, rowGap: 12, columnGap: 12 },
        }"
        :bottom-white-space="0"
      >
        <template #item="{ item }">
          <NovaBoxSearchUser :source="item" :on="false" />
        </template>
      </NovaRecyclerGrid>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-result-group {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.type-creator .search-result-group-header {
    background-color: $color-secondary-green-light-10;
  }
  &.type-user .search-result-group-header {
    background-color: $color-secondary-green-light-10;
  }

  &.type-premium .search-result-group-header {
    background-color: $color-secondary-purple-light-10;
  }

  &.type-post .search-result-group-header {
    background-color: $color-secondary-yellow-light-10;
  }

  &.type-comment .search-result-group-header {
    background-color: $color-secondary-red-light-10;
  }

  &.type-hash .search-result-group-header {
    background-color: $color-secondary-blue-light-10;
  }

  .search-result-group-header {
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;

    &.disabled {
      cursor: auto;

      .go-detail-indicator {
        display: none;
      }
    }

    > .label {
      @include text-style($text-display-bold);
      color: $color-text-2;
    }

    > .go-detail-indicator {
      display: flex;
      align-items: center;
      gap: 2px;
      @include text-style($text-body-12-regular);
      color: $color-text-3;
    }
  }

  .search-result-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
